import React, { useState, useEffect } from 'react';
import { Button, Form, Input, Space, Card, InputNumber, Divider } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { SubCampaignData } from '@/@types/CampaignData';
import styled from 'styled-components';

const SubCampaignContainer = styled.div`
  margin-bottom: 24px;
`;

const SubCampaignTitle = styled.h3`
  font-size: 16px;
  margin-bottom: 16px;
`;

const SubCampaignCard = styled(Card)`
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
`;

const SubCampaignFormItem = styled(Form.Item)`
  margin-bottom: 12px;
`;

interface SubCampaignComponentProps {
    form: any; // Form 인스턴스
    initialSubCampaigns?: SubCampaignData[];
    isAppType: boolean; // 'app' 타입의 sub_media인지 여부
    onChange?: (subCampaigns: SubCampaignData[]) => void; // 서브 캠페인 변경시 호출될 콜백
}

const SubCampaignComponent: React.FC<SubCampaignComponentProps> = ({
    form,
    initialSubCampaigns = [],
    isAppType,
    onChange
}) => {
    useEffect(() => {
        // 초기 서브 캠페인 데이터가 있으면 폼에 설정
        if (initialSubCampaigns && initialSubCampaigns.length > 0) {
            form.setFieldsValue({ sub_campaigns: initialSubCampaigns });
        }
    }, [initialSubCampaigns, form]);

    // 서브 캠페인 필드 변경 시 콜백 호출
    const handleFieldsChange = () => {
        const subCampaigns = form.getFieldValue('sub_campaigns') || [];
        if (onChange) {
            onChange(subCampaigns);
        }
    };

    return (
        <SubCampaignContainer>
            <Divider />
            <SubCampaignTitle>서브 캠페인 관리</SubCampaignTitle>

            <Form.List name="sub_campaigns">
                {(fields, { add, remove }) => (
                    <>
                        {fields.map(({ key, name, ...restField }) => (
                            <SubCampaignCard key={key}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    {/* ID 필드 (숨김 처리) */}
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'id']}
                                        hidden
                                    >
                                        <Input />
                                    </Form.Item>

                                    {/* parent_campaign_id 필드 (숨김 처리) */}
                                    <Form.Item
                                        {...restField}
                                        name={[name, 'parent_campaign_id']}
                                        hidden
                                    >
                                        <Input />
                                    </Form.Item>

                                    {/* 이벤트 이름 */}
                                    <SubCampaignFormItem
                                        {...restField}
                                        name={[name, 'event_name']}
                                        label="이벤트 이름 (mmp에서 추가 이벤트를 구분하기위한 식별 값)"
                                        rules={[
                                            { required: true, message: '이벤트 이름을 입력해주세요' }
                                        ]}
                                    >
                                        <Input
                                            placeholder="이벤트 이름"
                                            onChange={handleFieldsChange}
                                        />
                                    </SubCampaignFormItem>

                                    {/* 설명 (선택 사항) */}
                                    <SubCampaignFormItem
                                        {...restField}
                                        name={[name, 'description']}
                                        label="설명"
                                    >
                                        <Input
                                            placeholder="설명 (선택 사항)"
                                            onChange={handleFieldsChange}
                                        />
                                    </SubCampaignFormItem>

                                    <Space>
                                        {/* 콜당 포인트 */}
                                        <SubCampaignFormItem
                                            {...restField}
                                            name={[name, 'sub_points_per_call']}
                                            label="광고 단가 (vat별도)"
                                            rules={[
                                                { required: true, message: '광고 단가를 입력해주세요' }
                                            ]}
                                        >
                                            <InputNumber
                                                min={0}
                                                placeholder="광고 단가"
                                                onChange={handleFieldsChange}
                                                style={{ width: '150px' }}
                                            />
                                        </SubCampaignFormItem>

                                        {/* 참여자당 포인트 */}
                                        <SubCampaignFormItem
                                            {...restField}
                                            name={[name, 'sub_points_per_participant']}
                                            label="고객 리워드 (vat별도)"
                                            rules={[
                                                { required: true, message: '고객 리워드를 입력해주세요' }
                                            ]}
                                        >
                                            <InputNumber
                                                min={0}
                                                placeholder="고객 리워드"
                                                onChange={handleFieldsChange}
                                                style={{ width: '150px' }}
                                            />
                                        </SubCampaignFormItem>

                                        {/* 삭제 버튼 */}
                                        <Button
                                            type="text"
                                            danger
                                            icon={<MinusCircleOutlined />}
                                            onClick={() => {
                                                remove(name);
                                                handleFieldsChange();
                                            }}
                                            style={{ marginTop: '28px' }}
                                        >
                                            삭제
                                        </Button>
                                    </Space>
                                </Space>
                            </SubCampaignCard>
                        ))}

                        {/* 서브 캠페인 추가 버튼 */}
                        <Form.Item>
                            <Button
                                type="dashed"
                                onClick={() => {
                                    add({
                                        event_name: '',
                                        description: '',
                                        sub_points_per_call: 0,
                                        sub_points_per_participant: 0
                                    });
                                    handleFieldsChange();
                                }}
                                block
                                icon={<PlusOutlined />}
                            >
                                서브 캠페인 추가
                            </Button>
                        </Form.Item>
                    </>
                )}
            </Form.List>
        </SubCampaignContainer>
    );
};

export default SubCampaignComponent;